import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Public as PublicIcon,
  Quiz as QuizIcon,
  PlayArrow as PlayArrowIcon,
  ArrowForward as ArrowForwardIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import { useGetPublicMockTestsQuery } from "@/store/api/user/publicMockTestApi";
import ModeSelectDialog from "@/pages/user/PublicMockTest/ModeSelectDialog";

// Lavender brand palette — mirrors src/pages/user/PublicMockTest/index.js
const BRAND_GRADIENT =
  "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 50%, #c4b5fd 100%)";
const BRAND_GRADIENT_SOFT =
  "linear-gradient(135deg, #a78bfa 0%, #c4b5fd 50%, #ddd6fe 100%)";
const BRAND_PRIMARY = "#7c3aed";
const BRAND_TINT_BG = "rgba(139,92,246,0.08)";

const HOME_LIMIT = 3;

const MockTestPreviewCard = ({ exam, onStart }) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 3,
      background: "rgba(255,255,255,0.96)",
      border: "1px solid rgba(139,92,246,0.18)",
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 14px 28px rgba(124,58,237,0.18)",
        borderColor: "rgba(139,92,246,0.45)",
      },
    }}
  >
    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box
          sx={{
            p: 0.75,
            borderRadius: 2,
            background: BRAND_GRADIENT,
            display: "inline-flex",
            color: "white",
          }}
        >
          <QuizIcon fontSize="small" />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.3,
            fontSize: { xs: "0.95rem", sm: "1rem" },
            wordBreak: "break-word",
          }}
        >
          {exam.title}
        </Typography>
      </Box>
      {exam.book?.title && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {exam.book.title}
          {exam.book.year ? ` · ${exam.book.year}` : ""}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {exam.totalQuestions != null && (
          <Chip
            size="small"
            label={`${exam.totalQuestions} questions`}
            sx={{
              borderRadius: 2,
              color: BRAND_PRIMARY,
              fontWeight: 600,
              bgcolor: BRAND_TINT_BG,
              border: "1px solid",
              borderColor: BRAND_PRIMARY,
            }}
          />
        )}
        {Array.isArray(exam.availableParts) &&
          exam.availableParts.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`Parts ${exam.availableParts.join(", ")}`}
              sx={{ borderRadius: 2 }}
            />
          )}
      </Stack>
    </CardContent>
    <CardActions sx={{ p: 2, pt: 0 }}>
      <Button
        fullWidth
        startIcon={<PlayArrowIcon />}
        onClick={() => onStart(exam)}
        sx={{
          borderRadius: 2,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          color: "white",
          background: BRAND_GRADIENT,
          "&:hover": { background: BRAND_GRADIENT_SOFT, opacity: 0.95 },
        }}
      >
        Start free
      </Button>
    </CardActions>
  </Card>
);

const PublicMockTestSection = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetPublicMockTestsQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const [selectedExam, setSelectedExam] = useState(null);

  const tests = useMemo(
    () => (data?.items || []).slice(0, HOME_LIMIT),
    [data]
  );

  // Hide the section entirely when the API errors or returns nothing — no
  // empty placeholders on the marketing homepage.
  if (!isLoading && (isError || tests.length === 0)) return null;

  const handleStartFromDialog = ({ mode, parts }) => {
    const params = new URLSearchParams({ mode });
    if (mode === "part" && parts.length > 0) {
      params.set("parts", parts.join(","));
    }
    navigate(`/mock-tests/${selectedExam._id}/run?${params.toString()}`);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 4,
        background: "transparent",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              background: BRAND_GRADIENT,
              display: "inline-flex",
              boxShadow: "0 8px 18px rgba(124,58,237,0.28)",
            }}
          >
            <PublicIcon sx={{ color: "white" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
                component="h2"

            sx={{
              fontWeight: 700,
              background:
                "rgba(150, 90, 255)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textTransform: "uppercase",
            }}
            >
              Public Mock Tests
            </Typography>
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.25,
                color: BRAND_PRIMARY,
              }}
            >
              <LockOpenIcon sx={{ fontSize: 14 }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, letterSpacing: 0.3 }}
              >
                Free to try · no login required
              </Typography>
            </Box> */}
          </Box>
        </Box>
        <Button
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            navigate("/mock-tests");
            window.scrollTo({ top: 0 });
          }}
          sx={{
            alignSelf: { xs: "stretch", sm: "auto" },
            borderRadius: 2,
            px: 2,
            py: 0.75,
            fontWeight: 600,
            textTransform: "none",
            color: "white",
            background: BRAND_GRADIENT,
            boxShadow: "0 6px 14px rgba(124,58,237,0.22)",
            "&:hover": { background: BRAND_GRADIENT_SOFT, opacity: 0.95 },
          }}
        >
          Browse all
        </Button>
      </Box>

      {/* Body */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: HOME_LIMIT }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton
                variant="rectangular"
                height={170}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {tests.map((exam) => (
            <Grid item xs={12} sm={6} md={4} key={exam._id}>
              <MockTestPreviewCard exam={exam} onStart={setSelectedExam} />
            </Grid>
          ))}
        </Grid>
      )}

      <ModeSelectDialog
        open={!!selectedExam}
        exam={selectedExam}
        onClose={() => setSelectedExam(null)}
        onStart={handleStartFromDialog}
      />
    </Paper>
  );
};

export default PublicMockTestSection;
